// Orders view — every Shopify order tied to the customer with status pills,
// reorder buttons, and a detail drawer. Per spec §14.
//
// Phase B v1 reads from customer_designs grouped by last_ordered_order_id
// so each order shows up with its bundled line items. A future improvement
// is to query the dedicated /orders endpoint once it ships.

import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Package, Truck, CheckCircle, Cog, FileEdit } from 'lucide-react';
import { listDesigns, reorderDesign, dispatchCartAdd } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useStudio } from '../../lib/store';
import type { Design, DesignStatus } from '../../lib/types';
import { fmtDate } from '../../lib/format';
import { SkeletonGrid } from '../../components/LoadingSkeleton';
import { EmptyState } from '../../components/EmptyState';
import './orders.css';

interface OrderGroup {
  orderId: string;
  orderName: string;
  createdAt: string | null;
  status: DesignStatus;
  designs: Design[];
}

const STATUS_PILLS: Record<DesignStatus, { label: string; cls: string; Icon: typeof Cog }> = {
  draft:         { label: 'Draft',         cls: 'pill--draft',       Icon: FileEdit },
  ordered:       { label: 'Ordered',       cls: 'pill--ordered',     Icon: CheckCircle },
  in_production: { label: 'In production', cls: 'pill--production',  Icon: Cog },
  shipped:       { label: 'Shipped',       cls: 'pill--shipped',     Icon: Truck },
  delivered:     { label: 'Delivered',     cls: 'pill--delivered',   Icon: CheckCircle },
  cancelled:     { label: 'Cancelled',     cls: 'pill--cancelled',   Icon: FileEdit },
};

const FILTERS: Array<{ id: 'all' | DesignStatus; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'in_production', label: 'In production' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'delivered', label: 'Delivered' },
];

export function OrdersView() {
  const [filter, setFilter] = useState<typeof FILTERS[number]['id']>('all');
  const pushToast = useStudio((s) => s.pushToast);
  const setView = useStudio((s) => s.setView);

  const query = useQuery({
    queryKey: qk.designs({ view: 'orders' }),
    queryFn: () => listDesigns({ sort: 'recently_ordered', limit: 60 }),
  });

  const groups = useMemo(() => groupByOrder(query.data?.items ?? []), [query.data]);
  const filtered = filter === 'all' ? groups : groups.filter((g) => g.status === filter);

  const reorderMut = useMutation({
    mutationFn: async (id: string) => {
      const desc = await reorderDesign(id);
      return dispatchCartAdd(desc);
    },
    onSuccess: (url) => (window.location.href = url),
    onError: () => pushToast({ type: 'error', message: 'Could not reorder' }),
  });

  return (
    <section aria-label="Orders">
      <header className="view-head">
        <p className="eyebrow" style={{ color: 'var(--forest)', marginBottom: 8 }}>
          Order history
        </p>
        <h1>Orders</h1>
        <p className="sub">Every order you&rsquo;ve placed with us, with tracking and one-click reorder.</p>
      </header>

      <div className="orders__filters" role="tablist">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`chip${filter === f.id ? ' is-active' : ''}`}
            role="tab"
            aria-selected={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <SkeletonGrid count={4} variant="order" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No orders yet."
          body="Once you place your first order it'll show up here with tracking."
          ctaLabel="Browse products"
          onCta={() => setView('designs')}
        />
      ) : (
        <div className="orders__list">
          {filtered.map((g) => {
            const pill = STATUS_PILLS[g.status];
            const totalQty = g.designs.reduce((acc, d) => acc + (d.rollups.last_ordered_qty ?? 0), 0);
            return (
              <article key={g.orderId} className="order-row">
                <div className="order-row__thumb" aria-hidden="true">
                  {g.designs[0]?.thumbnail.url ? (
                    <img src={g.designs[0].thumbnail.url} alt="" />
                  ) : (
                    <Package size={24} strokeWidth={1.4} />
                  )}
                </div>
                <div className="order-row__info">
                  <div className="order-row__title">
                    {g.designs.map((d) => d.name).filter(Boolean).slice(0, 2).join(' · ')}
                    {g.designs.length > 2 ? ` + ${g.designs.length - 2} more` : ''}
                    {totalQty > 0 ? ` · ${totalQty} qty` : ''}
                  </div>
                  <div className="order-row__sub">
                    Order {g.orderName} · Placed {fmtDate(g.createdAt)} · {g.designs.length} design{g.designs.length === 1 ? '' : 's'}
                  </div>
                </div>
                <span className={`pill ${pill.cls}`}>
                  <pill.Icon size={11} strokeWidth={1.6} aria-hidden="true" />
                  {pill.label}
                </span>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => g.designs[0] && reorderMut.mutate(g.designs[0].id)}
                  disabled={!g.designs[0]}
                >
                  Reorder
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function groupByOrder(designs: Design[]): OrderGroup[] {
  const map = new Map<string, OrderGroup>();
  for (const d of designs) {
    const orderId = d.rollups.last_ordered_order_id;
    if (!orderId) continue;
    let g = map.get(orderId);
    if (!g) {
      g = {
        orderId,
        orderName: d.rollups.last_ordered_order_name ?? '—',
        createdAt: d.rollups.last_ordered_at,
        status: d.status,
        designs: [],
      };
      map.set(orderId, g);
    }
    g.designs.push(d);
    // Keep the most "advanced" status as the order's status — i.e., if any
    // line item has been delivered, the whole order reads delivered.
    if (statusRank(d.status) > statusRank(g.status)) g.status = d.status;
  }
  return Array.from(map.values()).sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

function statusRank(s: DesignStatus): number {
  return ['draft', 'cancelled', 'ordered', 'in_production', 'shipped', 'delivered'].indexOf(s);
}
